import { FunctionComponent, ReactElement, useEffect, useRef } from 'react';
import { useStoreMap } from 'effector-react';
import { useDroppable } from '@dnd-kit/core';
import { MenuItemType, MenuType } from '@nx-frontend/shared/types';
import { getMenuItemParams } from './store-maps';

interface MenuTreeItemDroppableProps {
  id: string;
  menuType: MenuType;
  level: number;
  children: ReactElement;
  isEmpty?: boolean;
  activeDragId?: string;
  hideBottomDropArea?: boolean;
  onDragOver?: () => void;
  disabledDropItemTypes?: MenuItemType[];
}

const MenuTreeItemDroppableEnabled: FunctionComponent<MenuTreeItemDroppableProps> = ({
  id,
  menuType,
  level,
  children,
  isEmpty,
  activeDragId,
  hideBottomDropArea,
  onDragOver,
}) => {
  const { isOver: isTopOver, setNodeRef: topDroppableNodeRef } = useDroppable({
    id: `menu-item-droppable-top-${id}${isEmpty ? '-empty' : ''}`,
    data: {
      originalId: id,
      menuType,
      isEmpty,
      isBefore: true,
    },
  });

  const { isOver: isBottomOver, setNodeRef: bottomDroppableNodeRef } = useDroppable({
    id: `menu-item-droppable-bottom-${id}${isEmpty ? '-empty' : ''}`,
    data: {
      originalId: id,
      menuType,
      isEmpty,
      isBefore: false,
    },
  });

  const showDroppableAreas = activeDragId && activeDragId !== id;

  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (isTopOver || isBottomOver) {
      timeoutRef.current = setTimeout(() => {
        if (onDragOver) {
          onDragOver();
        }
      }, 1000);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isTopOver, isBottomOver, onDragOver]);

  const paddingLeft = level * 16 + 'px';

  return (
    <div className="relative">
      {showDroppableAreas && (
        <div
          ref={topDroppableNodeRef}
          className="absolute left-0 z-10 w-full flex items-start"
          style={{ paddingLeft, top: '-2px', paddingBottom: '2px', height: 'calc(50% + 2px)' }}
        >
          {isTopOver && <div className="w-full h-1 bg-cyan-500"></div>}
        </div>
      )}

      {children}

      {showDroppableAreas && !hideBottomDropArea && (
        <div
          ref={bottomDroppableNodeRef}
          className="absolute left-0 z-10 w-full flex items-end"
          style={{ paddingLeft, bottom: '-2px', paddingTop: '2px', height: 'calc(50% + 2px)' }}
        >
          {isBottomOver && <div className="w-full h-1 bg-cyan-500"></div>}
        </div>
      )}
    </div>
  );
};

export const MenuTreeItemDroppable: FunctionComponent<MenuTreeItemDroppableProps> = (props) => {
  const { id, menuType, disabledDropItemTypes = [] } = props;
  const menuItem = useStoreMap(getMenuItemParams(id, menuType));

  if (disabledDropItemTypes.length && disabledDropItemTypes.includes(menuItem.type)) {
    return props.children;
  } else {
    return <MenuTreeItemDroppableEnabled {...props} />;
  }
};
